import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Award, Bell, Check } from "lucide-react";
import { EASE } from "@/components/motion";
import { useAuth } from "@/context/AuthContext";

const SEEN_KEY = "portal_seen_notifs";
const loadSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"); } catch { return []; } };

export function LevelBadge({ level }) {
  if (!level) return null;
  const next = level.levels[level.index + 1];
  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="level-badge-card">
      <div className="flex items-center gap-4">
        <motion.span initial={{ rotate: -20, scale: 0.6 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }} className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 via-blue-900 to-slate-900 text-amber-300 grid place-items-center shadow-lg"><Award className="w-7 h-7" /></motion.span>
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">Lencana · Level {level.index + 1}/6</p>
          <p className="text-xl font-black text-slate-900" data-testid="level-name">{level.name}</p>
          <p className="text-xs text-slate-500">{level.levels[level.index].desc}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-6 gap-1.5">
        {level.levels.map((l, i) => (
          <motion.div key={l.name} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.1 * i, duration: 0.5, ease: EASE }} className={`h-2 rounded-full origin-left ${l.achieved ? "bg-blue-600" : "bg-slate-100"}`} title={l.name} />
        ))}
      </div>
      {next && <p className="mt-3 text-xs text-slate-500">Berikutnya: <span className="font-bold text-slate-700">{next.name}</span> — {next.desc}</p>}
    </div>
  );
}

export function NotificationBell({ items = [] }) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(loadSeen);
  const { user } = useAuth();
  const navigate = useNavigate();
  const registered = !!(user?.nig && user?.approval_status === "approved");
  const unread = items.filter((n) => !seen.includes(n.id));

  const toggle = () => {
    const next = !open;
    if (next && unread.length) {
      const ids = items.map((n) => n.id);
      setSeen(ids);
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(ids)); } catch {}
    }
    setOpen(next);
  };

  const go = (n) => {
    setOpen(false);
    navigate(n.type === "renungan" && !registered ? "/register" : (n.link || "/portal"));
  };

  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notification-bell" className="relative w-12 h-12 rounded-full bg-white/15 border border-white/20 grid place-items-center hover:bg-white/25 active:scale-95 transition" aria-label="Notifikasi">
        <Bell className="w-5 h-5" />
        {unread.length > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-amber-400 text-slate-900 text-[10px] font-black grid place-items-center" data-testid="notification-count">{unread.length}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8 }} className="absolute right-0 top-14 z-50 w-72 bg-white text-slate-800 rounded-3xl shadow-2xl border border-slate-100 p-3" data-testid="notification-panel">
            <p className="px-2 py-1 text-xs font-bold uppercase tracking-widest text-slate-500">Pengingat</p>
            {items.length === 0 && <p className="px-2 py-4 text-sm text-slate-500 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />Tidak ada pengingat.</p>}
            {items.map((n) => (
              <button key={n.id} type="button" onClick={() => go(n)} className="block w-full text-left rounded-2xl px-3 py-3 hover:bg-slate-50" data-testid={`notification-${n.id}`}>
                <p className="text-sm font-bold text-slate-900">{n.title}</p><p className="text-xs text-slate-500">{n.body}</p>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
